import type { AbilityName, AbilityScores } from '@character-creator/types';

/**
 * Calculate the ability modifier for a given ability score
 * Formula: floor((score - 10) / 2)
 */
export function calculateModifier(score: number): number {
  return Math.floor((score - 10) / 2);
}

/**
 * Calculate all ability modifiers from ability scores
 */
export function calculateAllModifiers(scores: AbilityScores): Record<AbilityName, number> {
  return {
    strength: calculateModifier(scores.strength),
    dexterity: calculateModifier(scores.dexterity),
    constitution: calculateModifier(scores.constitution),
    intelligence: calculateModifier(scores.intelligence),
    wisdom: calculateModifier(scores.wisdom),
    charisma: calculateModifier(scores.charisma),
  };
}

/**
 * Point buy costs for each ability score value
 * Scores range from 8-15 in point buy
 */
export const POINT_BUY_COSTS: Record<number, number> = {
  8: 0,
  9: 1,
  10: 2,
  11: 3,
  12: 4,
  13: 5,
  14: 7,
  15: 9,
};

/**
 * Standard array values
 */
export const STANDARD_ARRAY = [15, 14, 13, 12, 10, 8] as const;

/**
 * Maximum points available for point buy
 */
export const POINT_BUY_TOTAL = 27;

/**
 * Point buy score range
 */
export const POINT_BUY_MIN = 8;
export const POINT_BUY_MAX = 15;

/**
 * Get ability scores as an array of values
 */
export function getScoreValues(scores: AbilityScores): number[] {
  return Object.values(scores);
}

/**
 * Calculate total point buy cost for a set of ability scores
 */
export function calculatePointBuyCost(scores: AbilityScores): number {
  return getScoreValues(scores).reduce((total: number, score: number) => {
    const cost = POINT_BUY_COSTS[score];
    if (cost === undefined) {
      // Score is outside point buy range
      return total + (score < POINT_BUY_MIN ? 0 : 9 + (score - 15) * 2);
    }
    return total + cost;
  }, 0);
}

/**
 * Check if ability scores are valid for point buy
 */
export function isValidPointBuy(scores: AbilityScores): boolean {
  const cost = calculatePointBuyCost(scores);
  const allInRange = getScoreValues(scores).every(
    (score) => score >= POINT_BUY_MIN && score <= POINT_BUY_MAX
  );
  return allInRange && cost <= POINT_BUY_TOTAL;
}

/**
 * Check if ability scores match standard array
 */
export function isValidStandardArray(scores: AbilityScores): boolean {
  const sortedScores = [...getScoreValues(scores)].sort((a, b) => b - a);
  return (
    sortedScores.length === STANDARD_ARRAY.length &&
    sortedScores.every((score, index) => score === STANDARD_ARRAY[index])
  );
}

/**
 * Get remaining points for point buy
 */
export function getRemainingPoints(scores: AbilityScores): number {
  return POINT_BUY_TOTAL - calculatePointBuyCost(scores);
}

/**
 * Default starting scores for point buy (all 8s)
 */
export const DEFAULT_POINT_BUY_SCORES: AbilityScores = {
  strength: 8,
  dexterity: 8,
  constitution: 8,
  intelligence: 8,
  wisdom: 8,
  charisma: 8,
};

/**
 * Format modifier as string with sign (+2, -1, etc.)
 */
export function formatModifier(modifier: number): string {
  return modifier >= 0 ? `+${modifier}` : `${modifier}`;
}

/**
 * Apply ASI (Ability Score Increase) choices to ability scores.
 * Each ability-increase choice adds to the relevant ability, capped at the given max (default 20).
 * Feat choices are ignored (they don't modify scores directly here).
 */
export function applyAsiIncreases(
  baseScores: AbilityScores,
  asiChoices: Record<number, { type: string; increases?: { ability: AbilityName; amount: number }[] }>,
  cap: number = 20,
): AbilityScores {
  const scores = { ...baseScores };

  for (const choice of Object.values(asiChoices)) {
    if (choice.type === 'ability-increase' && choice.increases) {
      for (const inc of choice.increases) {
        scores[inc.ability] = Math.min(cap, scores[inc.ability] + inc.amount);
      }
    }
  }

  return scores;
}

/**
 * Apply multiple sets of ASI choices in sequence.
 * Useful for multiclass characters where each class has its own ASI progression.
 */
export function applyMultipleAsiIncreases(
  baseScores: AbilityScores,
  asiChoicesArray: Record<number, { type: string; increases?: { ability: AbilityName; amount: number }[] }>[],
  cap: number = 20,
): AbilityScores {
  let scores = { ...baseScores };
  for (const asiChoices of asiChoicesArray) {
    scores = applyAsiIncreases(scores, asiChoices, cap);
  }
  return scores;
}
